import type {
  AgentParserFile,
  AgentParserFileRole,
  AgentParserScriptSlots,
  IngestMappingConfigSummary,
  OutputCheck,
  OutputTable,
  PageTable,
  ScriptFingerprint,
  TabularConfigBuildDetail,
  TabularConfigDraft,
  TabularConfigDraftDiff,
  TabularConfigExamplePreview,
  TabularConfigPagePreview
} from '@axilon-apps/domains-ontology-ui-state'
import { definitionDiff } from './definitionDiff'

/**
 * What the tabs read, whether the page shows a saved version or a draft. The
 * two differ in where the data lives (a `definition` row vs `result.draft`),
 * not in what the tabs need, so they are flattened once here.
 */
export interface ConfigSubject {
  kind: 'version' | 'draft'
  parserKind: 'script' | 'agent'
  description: string
  output: { tables: OutputTable[] }
  checks: OutputCheck[]
  script?: {
    script: string
    entrypoint: string
    imageVersion: string
    verifiedAt: string
    fingerprint: ScriptFingerprint
  }
  /** An agent parser's scripts in the order they run: preprocess, finalize, helpers, modules. */
  agentScripts?: {
    scripts: AgentParserFile[]
    verifiedAt: string
  }
  /** What the vision call reads from each page when a finalize script fills the output tables. */
  pageTables?: PageTable[]
  exampleNames: string[]
  /** Only a draft carries preview rows; a saved version shows columns only. */
  previews: TabularConfigExamplePreview[]
  pagePreviews: TabularConfigPagePreview[]
  notes: string[]
  /** A draft's builder diff, or History's *diff to previous* between two versions. */
  diff?: TabularConfigDraftDiff
  /** "v1": what `diff` compares against. */
  compareLabel?: string
  /** The held job's file that started an automatic rebuild — marked ● in the example switcher. */
  heldExampleName?: string
}

export function subjectForVersion(
  config: IngestMappingConfigSummary,
  compareTo?: IngestMappingConfigSummary
): ConfigSubject {
  const { definition } = config
  return {
    kind: 'version',
    parserKind: definition.parser.kind,
    description: definition.description,
    output: definition.output,
    checks: definition.checks,
    ...(definition.pageTables ? { pageTables: definition.pageTables } : {}),
    ...(definition.parser.kind === 'script'
      ? {
          script: {
            script: definition.parser.script,
            entrypoint: definition.parser.entrypoint,
            imageVersion: definition.parser.imageVersion,
            verifiedAt: definition.parser.verifiedAt,
            fingerprint: definition.parser.fingerprint
          }
        }
      : withAgentScripts(
          definition.parser,
          // Every script is stamped with the same verification.
          (
            definition.parser.preprocess ??
            definition.parser.finalize ??
            definition.parser.helpers?.[0] ??
            definition.parser.modules?.[0]
          )?.verifiedAt
        )),
    exampleNames: definition.examples.map((example) => example.name),
    previews: [],
    pagePreviews: [],
    notes: [],
    ...(compareTo
      ? {
          diff: definitionDiff(compareTo.definition, definition),
          compareLabel: `v${compareTo.version}`
        }
      : {})
  }
}

export function subjectForDraft(
  build: TabularConfigBuildDetail,
  draft: TabularConfigDraft,
  base: IngestMappingConfigSummary | undefined
): ConfigSubject {
  const baseNames = new Set(base?.definition.examples.map((example) => example.name) ?? [])
  const held =
    'parentJobId' in build.reason
      ? [...draft.examples].reverse().find((example) => !baseNames.has(example.name))
      : undefined
  return {
    kind: 'draft',
    parserKind: draft.parser.kind,
    description: draft.description,
    output: draft.output,
    checks: draft.checks,
    ...(draft.pageTables ? { pageTables: draft.pageTables } : {}),
    ...(draft.parser.kind === 'script'
      ? {
          script: {
            script: draft.parser.script,
            entrypoint: draft.parser.entrypoint,
            imageVersion: draft.parser.imageVersion,
            verifiedAt: draft.verification.verifiedAt,
            fingerprint: draft.parser.fingerprint
          }
        }
      : withAgentScripts(draft.parser, draft.verification.verifiedAt)),
    exampleNames: draft.examples.map((example) => example.name),
    previews: draft.previews,
    pagePreviews: draft.pagePreviews ?? [],
    notes: draft.notes,
    ...(draft.diff ? { diff: draft.diff } : {}),
    ...(draft.baseVersion !== undefined ? { compareLabel: `v${draft.baseVersion}` } : {}),
    ...(held ? { heldExampleName: held.name } : {})
  }
}

/**
 * Every script of an agent parser, in the order they run. Mirrors ingest's
 * `agentParserFiles`; that package's entry also loads Node-only modules, so the
 * browser imports only its types.
 */
export function agentScripts(parser: AgentParserScriptSlots): AgentParserFile[] {
  return [
    ...(parser.preprocess ? [script('preprocess', parser.preprocess)] : []),
    ...(parser.finalize ? [script('finalize', parser.finalize)] : []),
    ...(parser.helpers ?? []).map((helper) => ({
      ...script('helper', helper),
      usage: helper.usage
    })),
    ...(parser.modules ?? []).map((module) => script('module', module))
  ]
}

function withAgentScripts(
  parser: AgentParserScriptSlots,
  verifiedAt: string | undefined
): Pick<ConfigSubject, 'agentScripts'> {
  const scripts = agentScripts(parser)
  return scripts.length > 0 && verifiedAt ? { agentScripts: { scripts, verifiedAt } } : {}
}

function script(
  role: AgentParserFileRole,
  file: { entrypoint: string; script: string }
): AgentParserFile {
  return { role, entrypoint: file.entrypoint, script: file.script }
}
